import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Alert,
  CircularProgress,
  Slider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';

// Resim kırpma işlemleri için yardımcı fonksiyonlar
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

// Resmi belirtilen koordinatlara göre kırpma
async function getCroppedImg(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  targetWidth: number = 500,
  targetHeight: number = 500
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context is not available');
  }

  // Hedef boyutları ayarla
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  // Beyaz arka plan
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    targetWidth,
    targetHeight
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.95
    );
  });
}

interface CategoryImageModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  loading: boolean;
  title?: string;
  description?: string;
}

const CategoryImageModal: React.FC<CategoryImageModalProps> = ({ 
  open, 
  onClose, 
  onUpload, 
  loading,
  title,
  description
}) => {
  const { t } = useTranslation('salesSystem');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [showCropper, setShowCropper] = useState(false);

  const allowedFormats = ['jpg', 'jpeg', 'webp'];
  const cropSize = 500;
  
  // Resim türüne göre boyut ayarları
  const displayTitle = title || t('ecommerceCategoryImageModal.title');
  const isHeaderImage = displayTitle.toLowerCase().includes('header') || displayTitle.includes('Sayfa Üst Resmi');
  const aspectRatio = isHeaderImage ? 1050 / 145 : 1; // 7.24:1 vs 1:1
  const cropWidth = isHeaderImage ? 1050 : 500;
  const cropHeight = isHeaderImage ? 145 : 500;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileNameParts = file.name.split('.');
      const extension = fileNameParts[fileNameParts.length - 1].toLowerCase();

      if (allowedFormats.includes(extension)) {
        setSelectedFile(file);
        setFileError(null);
        
        // Dosya seçildiğinde kırpma aracını göster
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            setImageSrc(reader.result);
            setShowCropper(true);
          }
        };
        reader.readAsDataURL(file);
      } else {
        setSelectedFile(null);
        setImageSrc(null);
        setShowCropper(false);
        setFileError(t('ecommerceCategoryImageModal.errors.invalidFormat', { formats: allowedFormats.join(', ') }));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixelsValue: any) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  }, []);

  const handleUploadClick = async () => {
    if (imageSrc && croppedAreaPixels) {
      try {
        const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels, cropWidth, cropHeight);
        const originalFileName = selectedFile?.name || 'cropped-image.jpg';
        const fileExtension = originalFileName.split('.').pop() || 'jpg';
        const prefix = isHeaderImage ? 'header' : 'category';
        const croppedFile = new File(
          [croppedImageBlob], 
          `${prefix}-${Date.now()}.${fileExtension}`, 
          { type: `image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}` }
        );
        await onUpload(croppedFile);
        setSelectedFile(null);
        setImageSrc(null);
        setShowCropper(false);
        setFileError(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (error) {
        setFileError(t('ecommerceCategoryImageModal.errors.cropFailed'));
        console.error('Kırpma hatası:', error);
      }
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setImageSrc(null);
    setShowCropper(false);
    setFileError(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: showCropper ? 700 : 'auto',
          maxHeight: showCropper ? '90vh' : 'auto'
        }
      }}
    >
      <DialogTitle>
        {displayTitle}
        <IconButton
          aria-label={t('ecommerceCategoryImageModal.actions.close')}
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {description && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {description}
          </Alert>
        )}
        {showCropper && imageSrc ? (
          <Box sx={{ my: 2, height: 550, position: 'relative' }}>
            <Box sx={{ position: 'relative', width: '100%', height: 500, backgroundColor: '#f0f0f0' }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspectRatio}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                objectFit="contain"
                showGrid={true}
                style={{ 
                  containerStyle: { 
                    width: '100%', 
                    height: '100%' 
                  },
                  cropAreaStyle: {
                    border: '2px solid #fff',
                    boxShadow: '0 0 0 9999em rgba(0, 0, 0, 0.5)'
                  }
                }}
              />
            </Box>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <ZoomOutIcon sx={{ color: 'text.secondary' }} />
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e, zoom) => setZoom(zoom as number)}
                sx={{ mx: 2, flexGrow: 1 }}
              />
              <ZoomInIcon sx={{ color: 'text.secondary' }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              {t('ecommerceCategoryImageModal.cropHint', {
                width: cropWidth,
                height: cropHeight,
                shape: isHeaderImage ? t('ecommerceCategoryImageModal.shapes.rectangle') : t('ecommerceCategoryImageModal.shapes.square')
              })}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ my: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept={allowedFormats.map(f => `.${f}`).join(',')}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="category-image-input"
            />
            <label htmlFor="category-image-input">
              <Button
                variant="contained"
                component="span"
                fullWidth
                startIcon={<ImageIcon />}
              >
                {t('ecommerceCategoryImageModal.actions.selectImage')}
              </Button>
            </label>
            {fileError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {fileError}
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('ecommerceCategoryImageModal.note', {
                formats: allowedFormats.join(', '),
                width: cropWidth,
                height: cropHeight,
                shape: isHeaderImage ? t('ecommerceCategoryImageModal.shapes.rectangle') : t('ecommerceCategoryImageModal.shapes.square')
              })}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('ecommerceCategoryImageModal.actions.cancel')}
        </Button>
        <Button 
          onClick={handleUploadClick} 
          color="primary" 
          variant="contained"
          disabled={!imageSrc || !!fileError || loading || !croppedAreaPixels}
        >
          {loading ? <CircularProgress size={24} /> : t('ecommerceCategoryImageModal.actions.upload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryImageModal; 